// Linked-target resolution for `gundi:reference` annotations whose `target`
// is "provider" or "destination". See:
//   docs/superpowers/specs/2026-07-31-reference-data-config-ui-design.md §3
//     ("Provider resolution")
//   docs/superpowers/specs/2026-10-08-reference-destination-target-design.md
//   src/api/referenceOptions.ts (reused here for the per-target fetches)
//
// For target: "provider", the field's options come from the integration(s)
// on the other side of the Route(s) in which the configured integration
// is the *destination* — not from the integration being configured itself.
// For target: "destination", the mirror: the configured integration is the
// *provider*, and the options come from the destination(s) of its
// connection. Either way a target only counts if its integration *type*
// registered the named reference action. Targets lacking it are skipped; if
// none has it, the caller (ReferenceSelectWidget) falls back to free text.
import { createAxiosInstance, getQueryString, filter_page_size } from "./apiUtils";
import { fetchReferenceOptions, ReferenceDataResponse, ReferenceOption } from "./referenceOptions";
import { queryClient } from "./queryClient";
import { queryKeys } from "./queryKeys";

// Both lookups below live in the shared cache for 5 minutes: concurrent lookups
// for the same integration share one request, and a failure isn't kept, so a
// transient error doesn't wedge a target out of consideration. Integration
// details are shared by both directions: a destination's details are the same
// record whichever side asks.
const PROVIDER_DETAILS_TTL_MS = 300 * 1000;
// A lookup that hangs gives up after this, so the field can ask again
// instead of joining a request that never answers.
const LOOKUP_TIMEOUT_MS = 15000;

// Tests. Settled answers only — see clearReferenceOptionsCache.
export function clearProviderTargetsCache() {
    const settled = (q: { state: { fetchStatus: string } }) => q.state.fetchStatus !== "fetching";
    queryClient.removeQueries({ queryKey: [...queryKeys.referenceOptions.all, "provider-details"], predicate: settled });
    queryClient.removeQueries({ queryKey: [...queryKeys.referenceOptions.all, "provider-targets"], predicate: settled });
}

function fetchIntegrationDetailsCached(token: string, integrationId: string): Promise<any> {
    return queryClient.fetchQuery({
        queryKey: queryKeys.referenceOptions.providerDetails(integrationId),
        queryFn: async () => {
            const axiosInstance = await createAxiosInstance(token);
            const resp = await axiosInstance.get(`/v2/integrations/${encodeURIComponent(integrationId)}/`, { timeout: LOOKUP_TIMEOUT_MS });
            return resp.data;
        },
        staleTime: PROVIDER_DETAILS_TTL_MS,
    });
}

/** True iff the integration's type registered `actionValue` as a reference action. */
function supportsReferenceAction(integrationDetails: any, actionValue: string): boolean {
    const actions = integrationDetails?.type?.actions;
    if (!Array.isArray(actions)) return false;
    return actions.some((a: any) => a && a.value === actionValue && a.type === "reference");
}

// The Routes / connection lookup, cached so every menu open (and every row of a
// mapping array) doesn't re-ask it. Keyed by direction: with bidirectional
// routes the same integration can be a provider in one route and a destination
// in another, so the two directions must never share an entry.
function cachedTargets(key: string, resolve: () => Promise<string[]>): Promise<string[]> {
    return queryClient.fetchQuery({
        queryKey: queryKeys.referenceOptions.providerTargets(key),
        queryFn: resolve,
        // An empty answer is the one that changes under the user: the route
        // wizard sends them to the flow map to attach a destination and
        // promises the dropdown fills on the next edit. Keep asking until
        // there is something to remember.
        staleTime: (query) => ((query.state.data as string[] | undefined)?.length ? PROVIDER_DETAILS_TTL_MS : 0),
    });
}

/** Keep the ids whose integration type registers `actionValue` as a reference
 * action. A single integration whose details can't be fetched is skipped
 * (best-effort), never fatal. */
async function filterByReferenceSupport(token: string, ids: string[], actionValue: string): Promise<string[]> {
    const checked = await Promise.all(
        ids.map(async (id) => {
            try {
                const details = await fetchIntegrationDetailsCached(token, id);
                return supportsReferenceAction(details, actionValue) ? id : null;
            } catch {
                return null;
            }
        }),
    );
    return checked.filter((id): id is string => id !== null);
}

function uniqueIds(values: unknown[]): string[] {
    return Array.from(
        new Set(values.filter((id: unknown): id is string => typeof id === "string" && id.length > 0)),
    );
}

/** Resolve the provider integration ids that (a) are on the other side of a
 * Route where `destinationIntegrationId` is the destination, and (b)
 * whose integration type registers `actionValue` as a reference action.
 * Fetches Routes in a single, generously-limited page (per the spec:
 * "resolve the Routes in which the integration is a destination").
 * A failure fetching the Routes list itself propagates to the caller. */
export function resolveProviderTargets(
    token: string,
    destinationIntegrationId: string,
    actionValue: string,
): Promise<string[]> {
    // Not keyed by token: a new session remounts the widget anyway.
    return cachedTargets(`provider::${destinationIntegrationId}::${actionValue}`, async () => {
        const axiosInstance = await createAxiosInstance(token);
        const query_string = getQueryString({ destination_id: destinationIntegrationId });
        const resp = await axiosInstance.get(
            `/v2/connections/?pager=limit&limit=${filter_page_size}&${query_string}`,
            { timeout: LOOKUP_TIMEOUT_MS },
        );
        const routes: any[] = resp.data?.results ?? [];
        const providerIds = uniqueIds(routes.map((route) => route?.provider?.id));
        return filterByReferenceSupport(token, providerIds, actionValue);
    });
}

/** Resolve the destination integration ids of the connection whose provider
 * is `providerIntegrationId` and whose integration type registers
 * `actionValue` as a reference action. The connection details endpoint is
 * the same lookup an action runner makes to find its destinations, so the
 * two always agree on which integrations are in play. Its destination
 * summaries carry no type actions, so each destination's details are fetched
 * (cached) for the capability check, exactly as the provider path does.
 * A 404 means the integration is not a provider on any route yet and resolves
 * to no targets; any other failure fetching the connection propagates. */
export function resolveDestinationTargets(
    token: string,
    providerIntegrationId: string,
    actionValue: string,
): Promise<string[]> {
    return cachedTargets(`destination::${providerIntegrationId}::${actionValue}`, async () => {
        const axiosInstance = await createAxiosInstance(token);
        let resp: any;
        try {
            resp = await axiosInstance.get(`/v2/connections/${encodeURIComponent(providerIntegrationId)}/`, {
                timeout: LOOKUP_TIMEOUT_MS,
            });
        } catch (error: any) {
            // The connections endpoint only serves integrations that are a
            // provider on at least one route, so a brand-new provider 404s.
            // That is "no destination yet", not a failure to retry.
            if (error?.response?.status === 404) return [];
            throw error;
        }
        const destinations: any[] = resp.data?.destinations ?? [];
        const destinationIds = uniqueIds(destinations.map((destination) => destination?.id));
        return filterByReferenceSupport(token, destinationIds, actionValue);
    });
}

/** Fan the reference action out to each target (via fetchReferenceOptions, so
 * per-target results are TTL-cached and in-flight-deduped exactly like the
 * self-target path) and union the results deduped by option value (first
 * occurrence wins). `targetCount` is the number of targets that matched (not
 * the number that successfully returned options) — the widget uses
 * targetCount === 0 to decide whether to fall back to free text. */
async function fetchLinkedReferenceOptions(
    token: string,
    targetIds: string[],
    actionValue: string,
    params: Record<string, unknown>,
): Promise<ReferenceDataResponse & { targetCount: number }> {
    if (targetIds.length === 0) {
        return { options: [], cache_ttl_seconds: 300, truncated: false, targetCount: 0 };
    }

    const settled = await Promise.allSettled(
        targetIds.map((targetId) => fetchReferenceOptions(token, targetId, actionValue, params)),
    );

    const successes = settled.filter(
        (r): r is PromiseFulfilledResult<ReferenceDataResponse> => r.status === "fulfilled",
    );

    if (successes.length === 0) {
        // Every target fetch failed: rethrow (the first failure) so the
        // widget shows the same error degrade as the self-target path.
        throw (settled[0] as PromiseRejectedResult).reason;
    }

    const byValue = new Map<string, ReferenceOption>();
    let truncated = false;
    let minTtlSeconds: number | undefined;
    for (const { value: response } of successes) {
        truncated = truncated || !!response.truncated;
        const ttl = response.cache_ttl_seconds ?? 300;
        minTtlSeconds = minTtlSeconds === undefined ? ttl : Math.min(minTtlSeconds, ttl);
        for (const option of response.options ?? []) {
            if (!byValue.has(option.value)) byValue.set(option.value, option);
        }
    }

    return {
        options: Array.from(byValue.values()),
        cache_ttl_seconds: minTtlSeconds ?? 300,
        truncated,
        targetCount: targetIds.length,
    };
}

/** Provider-target equivalent of fetchReferenceOptions: the matching providers
 * of the Routes into `destinationIntegrationId`, fanned out and unioned. */
export async function fetchProviderReferenceOptions(
    token: string,
    destinationIntegrationId: string,
    actionValue: string,
    params: Record<string, unknown>,
): Promise<ReferenceDataResponse & { targetCount: number }> {
    const providerIds = await resolveProviderTargets(token, destinationIntegrationId, actionValue);
    return fetchLinkedReferenceOptions(token, providerIds, actionValue, params);
}

/** Destination-target equivalent of fetchReferenceOptions: the matching
 * destinations of `providerIntegrationId`'s connection, fanned out and unioned. */
export async function fetchDestinationReferenceOptions(
    token: string,
    providerIntegrationId: string,
    actionValue: string,
    params: Record<string, unknown>,
): Promise<ReferenceDataResponse & { targetCount: number }> {
    const destinationIds = await resolveDestinationTargets(token, providerIntegrationId, actionValue);
    return fetchLinkedReferenceOptions(token, destinationIds, actionValue, params);
}
